/**
 * RelationshipSection — manage parent and child relationships for a node.
 *
 * Lists parents and children separately with remove buttons.
 * "Add Parent" / "Add Child" open a searchable dropdown of available nodes.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { X, Plus, ChevronDown } from 'lucide-react';
import type { AdjacencyNode } from '../../types/adjacency';
import { INITIATIVE_COLORS } from '../../utils/map-colors';

interface RelationshipSectionProps {
  node: AdjacencyNode;
  allNodes: AdjacencyNode[];
  onAddParent: (parentId: string) => void;
  onAddChild: (childId: string) => void;
  onRemove: (targetId: string) => void;
}

export function RelationshipSection({ node, allNodes, onAddParent, onAddChild, onRemove }: RelationshipSectionProps) {
  const parentEdges = node.edges.filter(e => e.relationship === 'parent');
  const childEdges = node.edges.filter(e => e.relationship === 'child');

  const parentNodes = parentEdges
    .map(e => allNodes.find(n => n.id === e.targetId))
    .filter((n): n is AdjacencyNode => n != null);

  const childNodes = childEdges
    .map(e => allNodes.find(n => n.id === e.targetId))
    .filter((n): n is AdjacencyNode => n != null);

  // Nodes not already linked
  const linkedIds = new Set(node.edges.map(e => e.targetId));
  const available = allNodes.filter(n => n.id !== node.id && !linkedIds.has(n.id));

  return (
    <div className="space-y-3">
      {/* Parents */}
      <RelGroup
        label="Parents"
        items={parentNodes}
        onRemove={onRemove}
        onAdd={onAddParent}
        addLabel="Add Parent"
        available={available}
      />

      {/* Children */}
      <RelGroup
        label="Children"
        items={childNodes}
        onRemove={onRemove}
        onAdd={onAddChild}
        addLabel="Add Child"
        available={available}
      />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

interface RelGroupProps {
  label: string;
  items: AdjacencyNode[];
  onRemove: (id: string) => void;
  onAdd: (id: string) => void;
  addLabel: string;
  available: AdjacencyNode[];
}

function RelGroup({ label, items, onRemove, onAdd, addLabel, available }: RelGroupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!search) return available;
    const q = search.toLowerCase();
    return available.filter(n => n.name.toLowerCase().includes(q));
  }, [available, search]);

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-dim)' }}>
        {label} ({items.length})
      </div>

      {/* Node badges */}
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {items.map(n => (
            <span
              key={n.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]"
              style={{
                background: 'var(--glass-bg-elevated)',
                border: '1px solid var(--glass-border)',
                color: 'var(--text-secondary)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: INITIATIVE_COLORS[n.strategicInitiative] }} />
              {n.name.length > 22 ? n.name.slice(0, 21) + '\u2026' : n.name}
              <button
                onClick={() => onRemove(n.id)}
                className="ml-0.5 hover:opacity-80"
                style={{ color: 'var(--text-muted)' }}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-[10px] mb-1.5" style={{ color: 'var(--text-dim)' }}>None</div>
      )}

      {/* Add button + dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => { setIsOpen(!isOpen); setSearch(''); }}
          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-colors"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            color: 'var(--text-muted)',
          }}
        >
          <Plus size={10} />
          {addLabel}
          <ChevronDown size={10} />
        </button>

        {isOpen && (
          <div
            className="absolute left-0 top-full mt-1 w-56 max-h-48 rounded-lg overflow-hidden z-40"
            style={{
              background: 'var(--modal-bg-heavy)',
              border: '1px solid var(--glass-border)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}
          >
            <div className="p-1.5">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search nodes..."
                autoFocus
                className="w-full px-2 py-1 rounded text-[11px]"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--text-secondary)',
                  outline: 'none',
                }}
              />
            </div>
            <div className="max-h-36 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-[10px]" style={{ color: 'var(--text-dim)' }}>
                  No available nodes
                </div>
              ) : (
                filtered.map(n => (
                  <button
                    key={n.id}
                    onClick={() => {
                      onAdd(n.id);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className="w-full text-left px-3 py-1.5 text-[11px] flex items-center gap-2 transition-colors hover:bg-white/5"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: INITIATIVE_COLORS[n.strategicInitiative] }} />
                    <span className="truncate">{n.name}</span>
                    <span className="ml-auto text-[9px] shrink-0" style={{ color: 'var(--text-dim)' }}>
                      {n.adjacencyAxis}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
